import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { useEnroll } from "@/context/EnrollContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const { openEnroll } = useEnroll();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section on home page scroll
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return;
    }

    const sectionIds = NAV_LINKS
      .filter((l) => l.href.startsWith("#"))
      .map((l) => l.href.substring(1));

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const isActive = (link) => {
    if (link.href.startsWith("/")) {
      return location.pathname === link.href;
    }
    if (link.href.startsWith("#")) {
      return location.pathname === "/" && activeSection === link.href.substring(1);
    }
    return false;
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 shadow-sm backdrop-blur-md border-b border-border"
          : "bg-white/90 backdrop-blur-sm"
      }`}
    >
      <nav className="mx-auto flex h-24 sm:h-28 max-w-7xl items-center justify-between px-5 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src="/image/logo2.png"
            alt="Change Ur Story Logo"
            className="h-auto max-h-24 w-36 object-contain sm:max-h-28 sm:w-44"
          />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => {
            const active = isActive(l);
            const baseStyle = "header-menu-item relative py-1 transition-all";
            const activeStyle = active
              ? "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full"
              : "hover:opacity-70";

            return l.href.startsWith("/") ? (
              <Link key={l.href} to={l.href} className={`${baseStyle} ${activeStyle}`}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} className={`${baseStyle} ${activeStyle}`}>
                {l.label}
              </a>
            );
          })}
        </div>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-3 lg:flex">
          <Button variant="hero" size="lg" className="min-w-[170px] px-8 font-semibold" onClick={() => openEnroll("Professional")}>
            Try Chini Now
          </Button>
        </div>

        {/* Mobile hamburger */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-border bg-white">
              <div className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((l) => {
                  const active = isActive(l);
                  const mobileActiveStyle = active
                    ? "bg-primary/10"
                    : "hover:bg-secondary";

                  return l.href.startsWith("/") ? (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={() => setOpen(false)}
                      className={`header-menu-item rounded-lg px-3 py-3 transition-colors ${mobileActiveStyle}`}
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className={`header-menu-item rounded-lg px-3 py-3 transition-colors ${mobileActiveStyle}`}
                    >
                      {l.label}
                    </a>
                  );
                })}
                <Button
                  variant="hero"
                  size="lg"
                  className="mt-4"
                  onClick={() => { setOpen(false); openEnroll("Professional"); }}
                >
                  Try Chini Now
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
